/**
 * Shared formatting and small components for the core deposit feed screens
 * (GH #2911). Every value rendered here is read off a core-deposit-feed
 * response; nothing is defaulted, and an absent field renders as ABSENT.
 */

import { Badge, BodyText } from '@aaimplatform/ui';
import { BADGE_TONE, badge } from '../collateral/CollateralListParts';
import type { AmountTotal, DeliveryStatus } from '../../hooks/useCoreDepositFeed';

export const ABSENT = '--';

/** Status vocabulary from schema `DeliveryStatus`, rendered as the enum value. */
const DELIVERY_STATUS_BADGE: Record<DeliveryStatus, ReturnType<typeof badge>> = {
  OPEN: badge('pending', 'OPEN'),
  ACCEPTED: badge('active', 'ACCEPTED'),
  QUARANTINED: badge('error', 'QUARANTINED'),
  SUPERSEDED: badge('inactive', 'SUPERSEDED'),
};

export function DeliveryStatusBadge({ status }: { status: DeliveryStatus }) {
  const spec = DELIVERY_STATUS_BADGE[status];
  return <Badge variant={BADGE_TONE[spec.kind]}>{spec.label}</Badge>;
}

/** A date-only value, split rather than parsed so the viewer's zone cannot move it a day. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return ABSENT;
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${year}-${month}-${day}`;
}

/** An instant, pinned to UTC and labeled, as on the Tempo account screen. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return ABSENT;
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return ABSENT;
  return value.toLocaleString('en-US');
}

/**
 * Elapsed time from an instant to `nowMs`, in days, hours and minutes. The
 * amounts on the wire are decimal strings and the instants are ISO strings;
 * this is the one computed figure on the screens, and it is computed from
 * `receivedAt` alone. A `receivedAt` later than the browser clock is stated as
 * such rather than rounded to zero.
 */
export function formatAge(fromIso: string, nowMs: number): string {
  const fromMs = Date.parse(fromIso);
  if (Number.isNaN(fromMs)) return ABSENT;
  const diffMs = nowMs - fromMs;
  if (diffMs < 0) return 'later than this browser clock';
  const totalMinutes = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (days === 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  return parts.join(' ');
}

/**
 * One control total as `MEASURE amount currency`. The amount is the contract's
 * decimal string, rendered verbatim: converting it to a float would round a
 * value the reconciliation compares exactly.
 */
export function formatAmountTotal(total: AmountTotal): string {
  return `${total.measure} ${total.value.amount} ${total.value.currency}`;
}

export function AmountTotalList({ totals }: { totals: ReadonlyArray<AmountTotal> }) {
  if (totals.length === 0) {
    return <span className="text-tertiary">{ABSENT}</span>;
  }
  return (
    <ul className="space-y-0.5">
      {totals.map((total) => (
        <li key={total.measure} className="font-mono text-xs tabular-nums">
          {formatAmountTotal(total)}
        </li>
      ))}
    </ul>
  );
}

export function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-recessed last:border-b-0">
      <BodyText variant="ui" className="text-sm text-secondary font-mono break-words">
        {label}
      </BodyText>
      <BodyText variant="ui" className="text-sm text-primary font-mono tabular-nums text-right break-all">
        {value}
      </BodyText>
    </div>
  );
}

/**
 * The flag-off reading. The router sits behind requireOverdraftPreviewEnabled,
 * which answers 404 for an institution without the overdraft preview
 * entitlement, for a session with no tenant, and when its own entitlement read
 * fails; the contract's NotFound response does not say which.
 */
export const SURFACE_DARK_DESCRIPTION = [
  'The core deposit feed answered 404 for this session. It does so when the',
  'institution does not carry the overdraft preview entitlement, when the session',
  'carries no tenant, and when the entitlement read fails and the gate closes; the',
  'response does not say which.',
].join(' ');
